// El ARRANQUE del Agente (chat vacío): la mascota, el título con el verbo que se tipea,
// el composer en el medio y las sugerencias debajo —
// ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2.
//
// Es el empty state de "Asistente v2" llevado al Agente: mascota `idle` sobre un h1 en
// gradiente azul ("Creá / Preguntá / … sin límites"), el composer centrado (en la
// pantalla dedicada, alto como un párrafo) y los pills de sugerencia en azul claro. La
// nota de créditos va al pie, sin caja. Con la conversación andando este bloque no
// existe: ahí el composer vuelve al pie y los mensajes ocupan la columna.
//
// El composer entra como `children`: es la MISMA `BarraEntradaChat` que después vive
// abajo, montada acá mientras no hay mensajes.
//
// EN MOBILE el arranque tiene dos momentos: en reposo, mascota + título + caja; cuando
// el usuario toca la caja para escribir (`escribiendo`), la mascota y el título se van y
// las sugerencias aparecen ARRIBA de la caja, una debajo de la otra, que es lo que queda
// a la vista con el teclado abierto. En desktop no hay dos momentos: todo se ve siempre,
// y las sugerencias van debajo de la caja en UNA sola línea (la fila puede ser más ancha
// que la columna).
//
// Una sugerencia COMPLETA la caja con un pedido entero y deja el cursor ahí; no manda
// (ADR-2026-09-12-las-sugerencias-completan-la-caja).
//
// LA PRIMERA VEZ (el usuario todavía no tuvo ningún chat) esto no se dibuja: en su lugar va
// la pantalla de BIENVENIDA —mascota, una burbuja que se presenta y enumera, y un botón
// "Genial, empecemos"— y recién al tocarlo aparece el arranque de siempre
// (ADR-2026-09-14-la-bienvenida-del-agente).
//
// Sin caja y sin sugerencias a propósito: la bienvenida tiene UNA acción, y una caja de
// texto al lado de un botón son dos formas de seguir que compiten. Con la caja ahí, además,
// el mensaje largo se lee como un cartel que hay que esquivar para empezar a escribir.
//
// Y al tocar el botón la mascota VIAJA de donde estaba saludando a su lugar del arranque,
// en vez de teletransportarse: es la misma Cumbre, y que salte de un lado al otro la
// convierte en dos dibujos distintos. Ver `volarMascota`.

import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { VerboTipeado } from '../../pages/v2/modes/asistente/VerboTipeado';
import { MascotaConSaludo } from './MascotaAgente';
import {
  BIENVENIDA_AGENTE,
  GRADIENTE_TITULO,
  SUGERENCIAS_AGENTE,
  VERBOS_AGENTE,
} from './estilosAgente';

interface Props {
  /** Panel lateral (angosto) o pantalla dedicada. Cambia tamaños, no piezas. */
  variante: 'panel' | 'pantalla';
  /** Desde qué pantalla se abrió, si se sabe: una línea bajo el título. */
  contexto?: string | null;
  /** La nota de créditos al pie. En Baja el chat es gratis y la nota sería falsa. */
  muestraCosto: boolean;
  /** Mientras corre un turno las sugerencias no pisan lo que hay en la caja. */
  bloqueado: boolean;
  /** Completa la caja con el prompt entero de la sugerencia (no manda). */
  onSugerencia: (texto: string) => void;
  /** Ancho de la columna (el mismo que usa la conversación). */
  anchoColumna: string;
  /** El usuario tiene el foco en la caja (mobile: esconde el título, sube las sugerencias). */
  escribiendo?: boolean;
  /**
   * Primera vez en el Agente (ningún chat propio y sin cerrar la bienvenida en este
   * navegador): se dibuja la pantalla de bienvenida en lugar de todo lo demás. `false`
   * mientras no se sabe — ver `usePrimeraVezEnElAgente`.
   */
  bienvenida?: boolean;
  /** "Genial, empecemos": cierra la bienvenida y deja el arranque de siempre. */
  onEmpezar?: () => void;
  /** El composer. */
  children: ReactNode;
}

export const InicioAgente: React.FC<Props> = ({
  variante,
  contexto,
  muestraCosto,
  bloqueado,
  onSugerencia,
  anchoColumna,
  escribiendo = false,
  bienvenida = false,
  onEmpezar,
  children,
}) => {
  const pantalla = variante === 'pantalla';
  // Sugerencias: azul clarito con texto e ícono en `brand-primary`. El fondo va en hex
  // literal y no como `bg-brand-primary/10`: los tokens `brand-*` son un `var()` pelado y
  // el modificador de opacidad no emite una regla válida.
  //
  // `onMouseDown` con `preventDefault`: que el click NO le saque el foco al textarea. Sin
  // esto, en mobile el teclado se cierra al tocar la sugerencia y se vuelve a abrir al
  // completar (un parpadeo), y en desktop el botón se queda con el foco un instante.
  const pill = ({ key, label, prompt, Icon }: (typeof SUGERENCIAS_AGENTE)[number], clase: string) => (
    <button
      key={key}
      type="button"
      onClick={() => onSugerencia(prompt)}
      onMouseDown={(e) => e.preventDefault()}
      disabled={bloqueado}
      className={`inline-flex items-center gap-2 rounded-full bg-[#E9EDFD] py-2 px-4 text-[13.5px] font-semibold text-brand-primary transition-colors hover:bg-[#D8E0FB] disabled:cursor-not-allowed disabled:opacity-40 ${clase}`}
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
  /* ── La bienvenida ────────────────────────────────────────────────────────
     Una pantalla aparte y no una variante del arranque: no comparte casi nada con él —no
     hay caja, no hay sugerencias, no hay nota de créditos— y tejer un `bienvenida &&` por
     cada pieza dejaría un componente que dibuja dos cosas distintas y ninguna clara.

     `children` (el composer) NO se monta acá. Es lo que hace que no aparezca la caja; al
     tocar "Genial, empecemos" se monta con el resto del arranque, y no hay borrador que
     perder porque todavía no se escribió nada. */
  /* ── El vuelo de Cumbre ────────────────────────────────────────────────────
     Al tocar "Genial, empecemos" la mascota pasa de estar al lado del saludo a estar arriba
     del título del arranque. Son DOS elementos distintos en dos returns distintos, así que
     sin esto el cambio es un salto: desaparece de un lado y aparece en el otro.

     La técnica es FLIP y es exacta, no una aproximación: se guarda el rectángulo de la
     mascota ANTES de cambiar de pantalla (`empezar`), y en el layout effect —ya con la otra
     dibujada, antes de que el navegador pinte— se mide dónde quedó, se la corre con un
     `translate` hasta donde estaba, y se la deja volver. Por eso no hay ningún número de
     posición escrito a mano: si mañana el arranque mueve la mascota, el vuelo la sigue.

     Que la mascota mida LO MISMO en las dos pantallas es lo que permite que sea una
     traslación pura. Si alguna vez cambia de tamaño, acá hay que sumar la escala o el vuelo
     va a terminar con un tironcito.

     Con `prefers-reduced-motion` no vuela: aparece donde va, que es lo correcto.

     `el.animate` y no una clase CSS: no deja estado que limpiar ni una transición colgada
     sobre un elemento que después se usa para otra cosa. */
  const mascotaRef = useRef<HTMLSpanElement>(null);
  const desdeRef = useRef<DOMRect | null>(null);

  const empezar = () => {
    desdeRef.current = mascotaRef.current?.getBoundingClientRect() ?? null;
    onEmpezar?.();
  };

  useLayoutEffect(() => {
    // Sólo en el sentido bienvenida → arranque. Al revés no se vuelve.
    if (bienvenida) return;
    const desde = desdeRef.current;
    desdeRef.current = null;
    const el = mascotaRef.current;
    if (!desde || !el) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const hasta = el.getBoundingClientRect();
    const dx = desde.left - hasta.left;
    const dy = desde.top - hasta.top;
    // Sin viaje que valga la pena, no se anima: un `animate` de medio píxel es un frame
    // tirado y un parpadeo.
    if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;
    el.animate(
      [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }],
      // Corto y con salida suave: "sutil" es que se note que se movió, no que se espere.
      { duration: 420, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
    );
  }, [bienvenida]);

  if (bienvenida) {
    return (
      <div className="flex min-h-full flex-col">
        {/* El grupo se centra en el espacio que sobra y el botón queda al final: UNA
            estructura da las dos cosas que se pidieron —en mobile el botón pegado abajo, en
            desktop "mucho más abajo" del mensaje—, porque la pantalla es más alta y el
            mismo `flex-1` empuja más. Sin `mt-auto` ni alturas a mano. */}
        <div
          className={`mx-auto flex w-full flex-1 flex-col items-center justify-center ${anchoColumna}`}
        >
          {/* Cumbre Y la burbuja son UN grupo, con la forma de un mensaje de chat con su
              avatar: la mascota a la izquierda y el mensaje a su derecha, alineados por la
              BASE, en todos los tamaños.

              El aire entre los dos crece con la pantalla (`gap-3` → `md:gap-8`): en mobile
              cada píxel es ancho de texto, y en desktop sobra columna y pegados se leían
              como una sola mancha.

              La mascota NO cambia de tamaño entre esta pantalla y el arranque, y eso no es
              casual: es lo que deja que el vuelo de abajo sea una traslación pura, sin
              escala (ver `volarMascota`). */}
          <div className="flex w-full items-end justify-center gap-3 md:gap-8">
            <span ref={mascotaRef} className="shrink-0">
              <MascotaConSaludo
                size={120}
                burbuja="arriba"
                // El tooltip del hover dice casi lo mismo que la burbuja de al lado, y el
                // mismo texto dos veces en la misma pantalla se lee como un error, no como
                // énfasis.
                saludo={false}
                className={`h-auto shrink-0 ${pantalla ? 'w-[92px] md:w-[120px]' : 'w-[72px]'}`}
              />
            </span>
            {/* Con forma de MENSAJE DEL AGENTE: el mismo gris y el mismo radio que la
                burbuja del usuario en la conversación, pero espejada —la cola arriba a la
                izquierda— porque la manda él. Es lo primero que se ve del chat; conviene
                que ya tenga la forma de lo que se va a ver después. */}
            <p
              className={`min-w-0 rounded-[18px] rounded-tl-[6px] bg-[#F2F2F7] text-left leading-snug text-brand-black/90 ${
                pantalla
                  ? 'max-w-[560px] px-4 py-3 text-[15px] md:px-5 md:py-4 md:text-[16px]'
                  : 'px-4 py-3 text-[14.5px]'
              }`}
            >
              {BIENVENIDA_AGENTE}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 justify-center pt-8">
          <button
            type="button"
            onClick={empezar}
            // Padding 2:1 y sin alto fijo, como el resto de los botones anchos de la app.
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-primary py-3 px-6 text-[15px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-primary-hover active:scale-[0.99]"
          >
            Genial, empecemos
            <ArrowRight className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className={`mx-auto flex w-full flex-1 flex-col items-center justify-center ${anchoColumna}`}>
        {/* Mascota + título. La mascota saluda al pasar el mouse (easter egg); el h1 ya
            nombra la pantalla. En mobile se esconden mientras se escribe. */}
        <div
          className={`flex-col items-center ${escribiendo ? 'hidden md:flex' : 'flex'} ${
            pantalla ? 'gap-5 mb-10 md:mb-14' : 'gap-3 mb-7'
          }`}
        >
          {/* La MISMA ref que la de la bienvenida: es el destino del vuelo. React desmonta
              una y monta la otra en el mismo commit, y los refs se re-atan antes de los
              layout effects, así que cuando el vuelo mide ya apunta acá. */}
          <span ref={mascotaRef} className="shrink-0">
            <MascotaConSaludo
              size={120}
              burbuja="arriba"
              className={`h-auto shrink-0 ${pantalla ? 'w-[92px] md:w-[120px]' : 'w-[72px]'}`}
            />
          </span>
          {/* El verbo se tipea y cambia solo: para el lector de pantalla el título es
              una frase fija, no un deletreo. El gradiente va con `bg-clip-text` y el
              texto transparente; el cursor del verbo lleva color propio por eso mismo. */}
          <h1
            className={`bg-clip-text text-center font-normal leading-tight text-transparent ${
              pantalla ? 'text-[32px] md:text-[44px]' : 'text-[28px]'
            }`}
            style={{ backgroundImage: GRADIENTE_TITULO }}
            aria-label={`${VERBOS_AGENTE[0]} sin límites`}
          >
            <VerboTipeado className="font-sans font-semibold" verbos={VERBOS_AGENTE} />
            {/* El espacio va explícito: entre dos `{...}` JSX se come el salto de línea
                del código y las dos palabras quedarían pegadas. */}{' '}
            <span className="font-display italic">sin límites</span>
          </h1>
          {contexto && (
            <p className="-mt-1 text-center text-[13.5px] text-[#02062B]/[0.55]">
              Estás en {contexto}. Preguntame sobre eso, o pedime lo que quieras.
            </p>
          )}
        </div>

        {/* MOBILE, escribiendo: las sugerencias ARRIBA de la caja, apiladas una debajo
            de la otra y al ancho de la columna (con el teclado abierto no hay lugar para
            una fila que scrollea, y apiladas se leen de un vistazo). */}
        {escribiendo && (
          <div className="mb-3 flex w-full flex-col gap-2 md:hidden">
            {SUGERENCIAS_AGENTE.map((s) => pill(s, 'w-full justify-start'))}
          </div>
        )}

        <div className="w-full">{children}</div>

        {/* DESKTOP: debajo de la caja, SÓLO en la pantalla dedicada, en UNA sola línea
            aunque la fila mida más que la columna (el padre no recorta y las centra
            igual). En el panel del costado no van: ahí el agente acompaña una pantalla y
            las sugerencias generales sólo ocupaban lugar (pedido de Matías del 2026-09-18). */}
        {pantalla && (
          <div className="mt-6 hidden w-max max-w-[calc(100vw-2rem)] flex-nowrap justify-center gap-2 md:flex">
            {SUGERENCIAS_AGENTE.map((s) => pill(s, 'shrink-0'))}
          </div>
        )}
      </div>

      {/* La nota de créditos: al pie, centrada y sin caja. Es una nota, no un control. */}
      {muestraCosto && (
        <p className="shrink-0 pt-6 text-center text-[12.5px] font-medium leading-snug text-neutral-500">
          Atlas consume créditos según lo que uses
        </p>
      )}
    </div>
  );
};

export default InicioAgente;
